import { Check } from "@mui/icons-material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useCompanyScopeSelect, useSessionStore } from "@ryde/redux";
import {
  companyIdSelector,
  Log,
  onActivateKeyDown,
  profileModalOpenStateAtom,
  profileScopeAtom,
  type AuthorizationSet,
  type IProfileListCompanyDto,
} from "@ryde/shared";
import { greenMui } from "@ryde/theme";

import { ProfileModalCompanyItemStyled } from "./profile.modal.styled";

interface IProfileModalCompaniesItem {
  item: IProfileListCompanyDto;
}

export const ProfileModalCompaniesItem: FC<IProfileModalCompaniesItem> = ({
  item,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { applyCompanyScope } = useCompanyScopeSelect();
  const { rebootSession } = useSessionStore();
  const setProfileModalOpen = useSetRecoilState(profileModalOpenStateAtom);

  const profileScopeRecoil = useRecoilValue(profileScopeAtom);
  const currentCompanyId = useRecoilValue(companyIdSelector);
  const isCurrentCompany = useMemo(() => {
    return currentCompanyId === item.id;
  }, [currentCompanyId, item.id]);
  const currentRoleId = profileScopeRecoil?.roleId;

  const selectScope = useEvent((authSet: AuthorizationSet): void => {
    Log.debug(`Setting authorization scope from modal window to:`, authSet);
    setProfileModalOpen(false);

    const isSameCompany = authSet.companyId === currentCompanyId;

    void applyCompanyScope(authSet, () => {
      if (!isSameCompany) {
        const hasCompanyInPath = /^\/c\/[^/]+/.test(pathname);
        const newPath = hasCompanyInPath
          ? pathname.replace(/^\/c\/[^/]+/, `/c/${authSet.companyId}`)
          : `/c/${authSet.companyId}`;
        navigate({ pathname: newPath }, { replace: true });
      }
      rebootSession();
    });
  });

  return (
    <ProfileModalCompanyItemStyled>
      <label className="company_title">
        {isCurrentCompany && <Check sx={{ color: greenMui._600 }} />}
        {item.title}
      </label>
      <div className="role_chose_area">
        {item.authorizeSets?.map(authSet => {
          return (
            <div
              key={authSet.id}
              data-e2e="role-set-link"
              data-e2e-id={authSet.id}
              onClick={() => selectScope(authSet)}
              onKeyDown={onActivateKeyDown(() => selectScope(authSet))}
              role="button"
              tabIndex={0}
              className={clsx({
                role_set: true,
                active_role:
                  isCurrentCompany && currentRoleId === authSet.roleId,
              })}
            >
              {authSet.role}
            </div>
          );
        })}
      </div>
    </ProfileModalCompanyItemStyled>
  );
};
